import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FormEngine, RENDER_MODE, RENDER_TYPE, FIELD_TYPE, isTimezoneDateField } from '@fieldEngine';
import { uploadFileOnPublicApiV2 } from '@common/util';
import {
  createWorkkardRequest,
  updateWorkkardRequest,
  resetCreateWorkkard,
  resetUpdateWorkkard,
  resetWorkkardForm,
} from './store/workkardFormSlice';
import {
  selectIsCreatingWorkkard,
  selectCreateWorkkardError,
  selectCreateWorkkardFieldErrors,
  selectCreateWorkkardResult,
  selectCreatedWorkkardId,
  selectIsUpdatingWorkkard,
  selectUpdateWorkkardError,
  selectUpdateWorkkardFieldErrors,
  selectUpdateWorkkardResult,
} from './store/selectors';
import { mapBackendFieldErrors } from '../utils/mapBackendFieldErrors';
import { useWorkkardTemplates } from './hooks/useWorkkardTemplates';
import { useWorkkardFields } from './hooks/useWorkkardFields';
import { useCarriers } from './hooks/useCarriers';
import { useCurrentUserOwner } from './hooks/useCurrentUserOwner';
import { useWorkkardHydration } from './hooks/useWorkkardHydration';
import { useStaticFieldEnrichments } from './config/staticFieldEnrichments';
import { syncMailingAddress } from './utils/mailingAddressSync';
import { sortBySequence } from './utils/sortBySequence';
import { buildWorkkardPayload } from './utils/buildWorkkardPayload';
import { buildEditInitialValues } from './utils/buildEditInitialValues';
import {
  WORKKARD_FIELD_KEY,
  WORKKARD_FIELD_LABEL,
  MAILING_SAME_AS_ABOVE_FIELD_KEY,
  MAILING_ADDRESS_GROUP_LABEL,
  DEFAULT_WORKKARD_LIST_URL,
} from './constants';
import { getWorkkardDetailMasterData } from '../../WorkkardDetail/actions/commonAction';
import TemplateSelector from './components/TemplateSelector';
import FormActionsBar from './components/FormActionsBar';
import TimezoneWatcher from './components/TimezoneWatcher';
import DateTimezoneAdornment from './components/DateTimezoneAdornment';
import { CreateActivityLoader } from '../../../components/SkeletonLoader';
import DialogueBox from '../../../components/DialogueBox';
import ConfirmationPrompt from '../../../components/ConfirmationPrompt';
import Toast from '../../../components/Toast';
import { showToast, hideToast } from '../../SpockViewPage/actions/action';
import hiddenFieldsConfig from './config/hiddenFields.json';
import './styles/WorkkardCreation.scss';
import { PATHS } from '../../../router/paths';

const { hiddenFieldNameFragments } = hiddenFieldsConfig;

function isHiddenField(row) {
  const searchableFieldText = `${row.fieldName || ''} ${row.fieldKey || ''}`.toLowerCase();
  return hiddenFieldNameFragments.some((fragment) => searchableFieldText.includes(fragment));
}

function buildMailingSameAsAboveRow(isEdit, sequence) {
  return {
    fieldName: MAILING_SAME_AS_ABOVE_FIELD_KEY,
    fieldKey: MAILING_SAME_AS_ABOVE_FIELD_KEY,
    fieldType: FIELD_TYPE.CHECKBOX,
    label: '',
    sequence,
    isRequired: false,
    isNonEditable: false,
    isSystemField: false,
    group: '',
    fieldGroupName: '',
    value: !isEdit,
    defaultValue: true,
    items: [],
    preInitScript: '',
    presaveScript: '',
    settings: JSON.stringify({ inlineLabel: 'Mailing Address is same as above' }),
    description: '',
  };
}

function buildDescriptionRow() {
  return {
    fieldName: 'workkarddescription_richtext_static',
    fieldKey: WORKKARD_FIELD_KEY.DESCRIPTION,
    fieldType: FIELD_TYPE.RICH_TEXT,
    label: WORKKARD_FIELD_LABEL.DESCRIPTION,
    sequence: '9999',
    isRequired: true,
    isNonEditable: false,
    isSystemField: false,
    group: '',
    fieldGroupName: '',
    value: '',
    items: [],
    preInitScript: '',
    presaveScript: '',
    settings: '',
    description: 'Write a few lines to describe this Workkard. Please make sure it provides good insight about the Workkard for your vendors or for future reference.',
  };
}

const STATIC_WORKKARD_FIELD_ERROR_MAP = {
  workkardnumber: WORKKARD_FIELD_KEY.NUMBER,
  workkardtitle: WORKKARD_FIELD_KEY.TITLE,
  workkardowner: WORKKARD_FIELD_KEY.OWNER,
  carrier: WORKKARD_FIELD_KEY.CARRIER,
  workkarddescription: WORKKARD_FIELD_KEY.DESCRIPTION,
};

const WORKKARD_FILE_MAX_COUNT = 64;
const WORKKARD_FILE_MAX_SIZE  = 99 * 1024 * 1024;
const WORKKARD_INVALID_EXTS   = ['exe'];

const WORKKARD_FILE_SETTINGS = {
  uploader: uploadFileOnPublicApiV2,
  isSingleFile: false,
  maxFiles: WORKKARD_FILE_MAX_COUNT,
  maxSize:  WORKKARD_FILE_MAX_SIZE,
  invalidFiles: (ext) => WORKKARD_INVALID_EXTS.includes(String(ext).toLowerCase()),
  helpText: '',
  colWidth: 9,
};

function isFileField(row) {
  const t = String(row.fieldType || row.FieldType || '').toLowerCase();
  if (t === 'file') return true;
  const key = String(row.fieldKey || row.fieldName || '').toLowerCase();
  return /_file(_|$)/.test(key);
}

function enrichSystemField(row, enrichments) {
  const fieldNameKey = String(row.fieldName || '').trim().toLowerCase();
  const enrichmentConfig = enrichments[fieldNameKey];
  if (!enrichmentConfig) return null;

  const rowSettings = row.FieldSettings || row.fieldSettings || null;
  const mergedFieldSettings = (rowSettings && typeof rowSettings === 'object')
    ? { ...rowSettings, ...(enrichmentConfig.fieldSettings || {}) }
    : (enrichmentConfig.fieldSettings ?? null);

  return {
    ...row,
    fieldName: enrichmentConfig.fieldKey,
    fieldKey: enrichmentConfig.fieldKey,
    fieldType: enrichmentConfig.fieldType,
    label: row.label ?? row.Label ?? enrichmentConfig.label,
    description: (row.description || enrichmentConfig.description) || '',
    isRequired: row.isRequired ?? row.is_required ?? row.IsRequired ?? enrichmentConfig.isRequired ?? false,
    items: enrichmentConfig.items ?? row.items ?? [],
    FieldSettings: mergedFieldSettings,
  };
}

function buildDefaultRows(enrichments) {
  const defaultSystemFields = [
    { fieldName: 'Workkard Number', sequence: '1' },
    { fieldName: 'Workkard Title', sequence: '2' },
    { fieldName: 'Workkard Owner', sequence: '3' },
  ];
  return defaultSystemFields
    .map((defaultField) => enrichSystemField(defaultField, enrichments))
    .filter(Boolean)
    .map((row) => (row.fieldKey === WORKKARD_FIELD_KEY.TITLE ? { ...row, isRequired: false } : row))
    .concat([buildDescriptionRow()]);
}

function AddEditWorkkard({ isEdit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { id: workkardId } = useParams();

  const isCreatingWorkkard = useSelector(selectIsCreatingWorkkard);
  const createWorkkardError = useSelector(selectCreateWorkkardError);
  const createWorkkardFieldErrors = useSelector(selectCreateWorkkardFieldErrors);
  const createWorkkardResult = useSelector(selectCreateWorkkardResult);
  const createdWorkkardId = useSelector(selectCreatedWorkkardId);

  const isUpdatingWorkkard = useSelector(selectIsUpdatingWorkkard);
  const updateWorkkardError = useSelector(selectUpdateWorkkardError);
  const updateWorkkardFieldErrors = useSelector(selectUpdateWorkkardFieldErrors);
  const updateWorkkardResult = useSelector(selectUpdateWorkkardResult);

  const toast = useSelector((state) => state.toast);

  const { workkardData, isLoading: isLoadingWorkkard, error: fetchWorkkardError } = useWorkkardHydration(isEdit ? workkardId : null);

  const loadedWorkkardId = useSelector((state) => state.workkardDetail?.workkardDetail?.Id);

  useEffect(() => {
    if (isEdit && workkardId && String(loadedWorkkardId) !== String(workkardId)) {
      dispatch(getWorkkardDetailMasterData({ cId: workkardId }));
    }
  }, [isEdit, workkardId, loadedWorkkardId, dispatch]);

  const {
    templates,
    areTemplatesLoading,
    selectedTemplateShortName,
    setSelectedTemplateShortName,
    selectedTemplateName,
  } = useWorkkardTemplates({ isEdit });

  const renderType = isEdit ? 'edit' : 'new';
  const { workkardFields, areFieldsLoading } = useWorkkardFields(selectedTemplateShortName, renderType);

  useEffect(() => {
    if (isEdit && workkardData?.workkard_short_name && !selectedTemplateShortName) {
      setSelectedTemplateShortName(workkardData.workkard_short_name);
    }
  }, [isEdit, workkardData, selectedTemplateShortName, setSelectedTemplateShortName]);
  const { carriers } = useCarriers();
  const { currentUser, currentUserId, users } = useCurrentUserOwner();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const hiddenSubmitRef = useRef(null);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [pendingTemplateShortName, setPendingTemplateShortName] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const handleTemplateSelect = (shortName) => {
    if (isFormDirty) {
      setPendingTemplateShortName(shortName);
    } else {
      setSelectedTemplateShortName(shortName);
    }
  };

  const confirmTemplateChange = () => {
    setSelectedTemplateShortName(pendingTemplateShortName);
    setPendingTemplateShortName(null);
  };

  const cancelTemplateChange = () => setPendingTemplateShortName(null);

  const staticFieldEnrichments = useStaticFieldEnrichments({
    users,
    carriers,
    currentUserId,
    currentUser,
    isEdit,
  });

  const templateFormRows = useMemo(() => {
    if (!selectedTemplateShortName) {
      return buildDefaultRows(staticFieldEnrichments);
    }

    const enrichedFieldRows = workkardFields
      .filter((row) => !isHiddenField(row))
      .map((row) => {
        if (row.isSystemField) {
          return enrichSystemField(row, staticFieldEnrichments);
        }
        if (isTimezoneDateField(row)) {
          return {
            ...row,
            FieldSettings: {
              ...(row.FieldSettings || {}),
              rightAdornment: () => <DateTimezoneAdornment />,
            },
          };
        }
        if (isFileField(row)) {
          return {
            ...row,
            FieldSettings: {
              ...(row.FieldSettings || {}),
              ...WORKKARD_FILE_SETTINGS,
            },
          };
        }
        return row;
      })
      .filter(Boolean);

    const mailingAddressSequences = enrichedFieldRows
      .filter((field) => String(field.fieldGroupName || '').trim().toLowerCase() === MAILING_ADDRESS_GROUP_LABEL.toLowerCase())
      .map((field) => Number(field.sequence ?? field.Sequence ?? 0));
    const hasMailingGroup = mailingAddressSequences.length > 0;
    const checkboxSequence = hasMailingGroup ? Math.min(...mailingAddressSequences) - 0.5 : null;
    const fieldRowsWithMailingCheckbox = hasMailingGroup
      ? [...enrichedFieldRows, buildMailingSameAsAboveRow(isEdit, checkboxSequence)]
      : enrichedFieldRows;

    const hasDescription = fieldRowsWithMailingCheckbox.some(
      (field) => field.fieldKey === WORKKARD_FIELD_KEY.DESCRIPTION
        || String(field.fieldName || '').trim().toLowerCase() === 'workkard description'
    );
    const fieldRowsWithDescription = hasDescription
      ? fieldRowsWithMailingCheckbox
      : [...fieldRowsWithMailingCheckbox, buildDescriptionRow()];

    return sortBySequence(fieldRowsWithDescription);
  }, [selectedTemplateShortName, staticFieldEnrichments, workkardFields]);

  const returnUrl = isEdit
    ? (location.state?.returnUrl ?? `${PATHS.WORKKARD_DETAIL}?cId=${workkardId}#an`)
    : (location.state?.returnUrl ?? DEFAULT_WORKKARD_LIST_URL);

  const handleCancel = () => {
    if (isEdit || isFormDirty) {
      setShowCancelConfirm(true);
    } else {
      navigate(returnUrl);
    }
  };

  const confirmCancel = () => {
    setShowCancelConfirm(false);
    navigate(returnUrl);
  };

  const dismissCancelConfirm = () => setShowCancelConfirm(false);

  const handleFormSubmit = async (formData) => {
    const syncedFormData = syncMailingAddress({
      ...formData,
      templateShortName: selectedTemplateShortName,
    });
    const apiPayload = buildWorkkardPayload({
      formData: syncedFormData,
      templateShortName: selectedTemplateShortName,
      fields: workkardFields,
      isEdit,
    });

    if (isEdit) {
      dispatch(updateWorkkardRequest({ id: workkardId, data: { workkard: apiPayload.workkard } }));
    } else {
      dispatch(createWorkkardRequest(apiPayload));
    }
  };

  useEffect(() => {
    setIsSubmitting(isEdit ? isUpdatingWorkkard : isCreatingWorkkard);
  }, [isEdit, isCreatingWorkkard, isUpdatingWorkkard]);

  // The created claim is readable -- either its socket event arrived or the
  // poll fallback found it. Only now is it safe to open the detail page.
  useEffect(() => {
    if (createdWorkkardId) {
      dispatch(resetCreateWorkkard());
      dispatch(showToast({ message: 'Workkard created successfully.', type: 'success', delayTime: 5000, autoClose: true }));
      navigate(`${PATHS.WORKKARD_DETAIL}?cId=${createdWorkkardId}#an`);
    }
  }, [createdWorkkardId, dispatch, navigate]);

  // Fallback for a response that carried neither a correlation id nor a
  // workkard id, where there is nothing to wait on.
  useEffect(() => {
    if (createWorkkardResult) {
      dispatch(resetCreateWorkkard());
      dispatch(showToast({ message: 'Workkard created successfully.', type: 'success', delayTime: 5000, autoClose: true }));
      navigate(DEFAULT_WORKKARD_LIST_URL);
    }
  }, [createWorkkardResult, dispatch, navigate, returnUrl]);

  useEffect(() => {
    if (updateWorkkardResult) {
      dispatch(resetUpdateWorkkard());
      dispatch(showToast({ message: 'Workkard updated successfully.', type: 'success', delayTime: 5000, autoClose: true }));
      navigate(`${PATHS.WORKKARD_DETAIL}?cId=${workkardId}#an`);
    }
  }, [updateWorkkardResult, navigate, workkardId]);

  // TODO: error toast disabled for now, revisit and implement later. Under-field
  // error display (backendFieldErrors via mapBackendFieldErrors) still applies.
  useEffect(() => {
    if (createWorkkardError) {
      console.error('[AddEditWorkkard] Create failed:', createWorkkardError);
      // dispatch(showToast({ message: createWorkkardError, type: 'error', delayTime: 5000, autoClose: true }));
    }
  }, [createWorkkardError, dispatch]);

  useEffect(() => {
    if (updateWorkkardError) {
      console.error('[AddEditWorkkard] Update failed:', updateWorkkardError);
      // dispatch(showToast({ message: updateWorkkardError, type: 'error', delayTime: 5000, autoClose: true }));
    }
  }, [updateWorkkardError, dispatch]);

  useEffect(() => () => {
    dispatch(resetWorkkardForm());
  }, [dispatch]);

  const onSaveClick = () => hiddenSubmitRef.current?.click();

  const editInitialValues = useMemo(() => {
    if (!isEdit || !workkardData || !workkardFields.length) return null;
    return buildEditInitialValues(workkardData, workkardFields);
  }, [isEdit, workkardData, workkardFields]);

  const initialValues = isEdit
    ? editInitialValues
    : {
      ...(currentUserId ? { [WORKKARD_FIELD_KEY.OWNER]: currentUserId } : {}),
      'propertylocation_country_country': 'US',
      'mailingaddress_country_country': 'US',
      [MAILING_SAME_AS_ABOVE_FIELD_KEY]: true,
    };

  const isLoading = isEdit ? (isLoadingWorkkard || areFieldsLoading) : areFieldsLoading;
  const hasError = isEdit ? fetchWorkkardError : null;

  const backendFieldErrors = useMemo(
    () => mapBackendFieldErrors(
      isEdit ? updateWorkkardFieldErrors : createWorkkardFieldErrors,
      workkardFields,
      STATIC_WORKKARD_FIELD_ERROR_MAP,
    ),
    [isEdit, updateWorkkardFieldErrors, createWorkkardFieldErrors, workkardFields],
  );

  return (
    <div className="workkard-creation-page" data-test="workkard-form-page">
      {!isEdit && (
        <TemplateSelector
          templates={templates}
          selectedTemplateShortName={selectedTemplateShortName}
          onSelect={handleTemplateSelect}
          disabled={areTemplatesLoading}
        />
      )}

      <div className="workkard-creation-content-body">
        <div>
          {hasError && (
            <div className="errorMsg" role="alert" data-test="workkard-form-load-error">
              Failed to load workkard: {hasError}
            </div>
          )}
          {isLoading
            ? <CreateActivityLoader count={1} />
            : initialValues && (
              <FormEngine
                key={`${selectedTemplateShortName ?? 'default'}::${currentUserId}::${isEdit ? workkardId : 'new'}`}
                templateId={selectedTemplateShortName}
                templateShortName={selectedTemplateName ?? selectedTemplateShortName}
                rawRows={templateFormRows}
                initialValues={initialValues}
                renderMode={isEdit ? RENDER_MODE.EDIT : RENDER_MODE.CREATE}
                renderType={RENDER_TYPE.WORKKARD}
                showSubmitButton={false}
                onSubmit={handleFormSubmit}
                onDirtyChange={setIsFormDirty}
                fieldErrors={backendFieldErrors}
                footer={
                  <button
                    ref={hiddenSubmitRef}
                    type="submit"
                    className="workkard-creation-hidden-submit"
                    aria-hidden="true"
                    tabIndex={-1}
                  />
                }
              >
                <TimezoneWatcher />
              </FormEngine>
            )
          }

          <FormActionsBar
            isSubmitting={isSubmitting}
            onSave={onSaveClick}
            onCancel={handleCancel}
          />
        </div>
      </div>

      <DialogueBox
        overlayClassName="modal"
        contentClassName="modalContent animated faster fadeInUpx2 small"
        shouldCloseOnOverlayClick={false}
        isOpen={!!pendingTemplateShortName}
        onRequestClose={cancelTemplateChange}
      >
        <ConfirmationPrompt
          message="You are trying to  change the template, all the entered data will be lost."
          onConfirmClick={confirmTemplateChange}
          onCancelClick={cancelTemplateChange}
          confirmationText="Confirm"
          cancelText="Cancel"
          class="modalBody"
          footerClass="modalFooter"
        />
      </DialogueBox>

      <DialogueBox
        overlayClassName="modal"
        contentClassName="modalContent animated faster fadeInUpx2 small"
        shouldCloseOnOverlayClick={false}
        isOpen={showCancelConfirm}
        onRequestClose={dismissCancelConfirm}
      >
        <ConfirmationPrompt
          message={isEdit
            ? "Do you really want to cancel the edit process of this Workkard?"
            : "Do you really want to cancel the Workkard creation process? All the populated data will be lost."}
          onConfirmClick={confirmCancel}
          onCancelClick={dismissCancelConfirm}
          confirmationText="Confirm"
          cancelText="Cancel"
          class="modalBody"
          footerClass="modalFooter"
        />
      </DialogueBox>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          delayTime={toast.delayTime}
          closeToast={() => dispatch(hideToast())}
          show={toast.show}
          entityType={toast.entityType}
          cId={toast.cId}
          actId={toast.actId}
          showActions={toast.showActions}
          isCloseIcon={toast.isCloseIcon}
          autoClose={toast.autoClose}
        />
      )}
    </div>
  );
}

export default AddEditWorkkard;
